/**
 * Section wrapper for legal pages with a hero banner + sticky ToC + prose content.
 */
import { useEffect, useState } from "react";
import { useLanguage, pick } from "@/lib/i18n";
import { LAST_UPDATED } from "@/lib/translations";

const LABELS = {
    en: { lastUpdated: "Last updated", contents: "On this page" },
    de: { lastUpdated: "Zuletzt aktualisiert", contents: "Auf dieser Seite" },
};

export default function LegalPageShell({ title, subtitle, sections, lastUpdated = LAST_UPDATED, children }) {
    const { lang } = useLanguage();
    const labels = pick(LABELS, lang);
    const [active, setActive] = useState(sections?.[0]?.id);

    useEffect(() => {
        if (!sections?.length) return;
        const handler = () => {
            const fromTop = window.scrollY + 160;
            let current = sections[0].id;
            for (const s of sections) {
                const el = document.getElementById(s.id);
                if (el && el.offsetTop <= fromTop) current = s.id;
            }
            setActive(current);
        };
        handler();
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, [sections]);

    return (
        <>
            <section className="relative bg-jungle-stripes text-cream-50 overflow-hidden">
                <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-amber-500/20 blur-3xl" />
                <div className="absolute -left-10 -bottom-10 w-72 h-72 rounded-full bg-jungle-500/20 blur-3xl" />
                <div className="relative max-w-6xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
                    <p className="font-display uppercase tracking-[0.2em] text-amber-400 text-xs sm:text-sm mb-3">
                        Dino Detective
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="mt-4 text-cream-50/85 max-w-2xl text-base sm:text-lg leading-relaxed">
                            {subtitle}
                        </p>
                    )}
                    <p className="mt-6 text-xs sm:text-sm text-amber-400 font-semibold">
                        {labels.lastUpdated}: {lastUpdated}
                    </p>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-5 sm:px-6 py-12 sm:py-16 grid lg:grid-cols-12 gap-8">
                {sections?.length > 0 && (
                    <aside className="lg:col-span-3 order-2 lg:order-1">
                        <div className="lg:sticky lg:top-24">
                            <div className="rounded-3xl border-2 border-jungle-700/10 bg-white p-5 shadow-soft">
                                <p className="font-display uppercase tracking-wider text-xs text-ink-300 mb-3">
                                    {labels.contents}
                                </p>
                                <nav className="grid gap-1">
                                    {sections.map((s) => (
                                        <a
                                            key={s.id}
                                            href={`#${s.id}`}
                                            data-testid={`toc-${s.id}`}
                                            className={
                                                "px-3 py-2 rounded-xl text-sm font-semibold transition-colors " +
                                                (active === s.id
                                                    ? "bg-jungle-700 text-cream-50"
                                                    : "text-ink-700 hover:bg-jungle-50 hover:text-jungle-900")
                                            }
                                        >
                                            {s.label}
                                        </a>
                                    ))}
                                </nav>
                            </div>
                        </div>
                    </aside>
                )}
                <article
                    data-testid="legal-content"
                    className={
                        "order-1 lg:order-2 " +
                        (sections?.length > 0 ? "lg:col-span-9" : "lg:col-span-12")
                    }
                >
                    <div className="rounded-3xl bg-white border-2 border-jungle-700/10 shadow-soft p-6 sm:p-10 prose-legal">
                        {children}
                    </div>
                </article>
            </section>
        </>
    );
}
